import { useEffect, useRef } from "react";
import { Eraser } from "lucide-react";

/**
 * Canvas signature pad. Uses pointer events so it works with mouse, touch, pen.
 * Emits a base64 PNG data URL via `onChange(dataUrl)` on every stroke end.
 */
export default function SignaturePad({ value, onChange, height = 160, disabled = false, testid = "signature-pad" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const initialisedRef = useRef(false);
  const loadedValueRef = useRef("");

  // Initialise canvas ONCE (size + drawing context). Resizing here on every value
  // change would blank the strokes the user just drew.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || initialisedRef.current) return;
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = getComputedStyle(document.documentElement).getPropertyValue("--fl-text").trim() || "#111";
    ctx.lineWidth = 2;
    initialisedRef.current = true;
  }, []);

  // Load an externally-provided data URL (e.g. an existing signature from the
  // backend). We only redraw when the incoming `value` differs from what we
  // have already painted — this preserves the user's live strokes.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !initialisedRef.current) return;
    if (!value) {
      // Explicit clear from parent.
      if (loadedValueRef.current) {
        const ctx = c.getContext("2d");
        ctx.clearRect(0, 0, c.width, c.height);
        loadedValueRef.current = "";
      }
      return;
    }
    if (value === loadedValueRef.current) return; // already painted (including our own strokes)
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0, rect.width, rect.height);
      loadedValueRef.current = value;
    };
    img.src = value;
  }, [value]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    drawing.current = true;
    last.current = pos(e);
    e.target.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    // Remember what we just painted so the value-sync effect above doesn't
    // repaint (which would flash / clear the canvas).
    loadedValueRef.current = dataUrl;
    onChange?.(dataUrl);
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    loadedValueRef.current = "";
    onChange?.("");
  };

  return (
    <div className="fl-card p-3" data-testid={testid}>
      <div
        className="rounded-md border overflow-hidden touch-none"
        style={{ borderColor: "var(--fl-border)", background: "color-mix(in oklab, var(--fl-bg-2) 92%, transparent)", height }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          onPointerCancel={end}
          style={{ width: "100%", height: "100%", cursor: disabled ? "not-allowed" : "crosshair", display: "block" }}
          data-testid={`${testid}-canvas`}
        />
      </div>
      <div className="flex items-center justify-between mt-2 text-xs" style={{ color: "var(--fl-muted-2)" }}>
        <span>Sign above using mouse, finger or stylus.</span>
        <button
          type="button"
          onClick={clear}
          disabled={disabled}
          className="inline-flex items-center gap-1 hover:text-[color:var(--fl-orange)] transition-colors disabled:opacity-40"
          data-testid={`${testid}-clear`}
        >
          <Eraser size={12} /> Clear
        </button>
      </div>
    </div>
  );
}
